import { TGRSymbolSpineAnimateBehaviour } from "behaviours/tgr-symbol-spine-animate-symbol-behavior";
import { TGRMatrixComponent } from "components/tgr-matrix";
import { TGRSymbolComponent } from "components/tgr-symbol";
import { PartnerConnectManager } from "partner-connect/partner-connect-manager";
import { MatrixFactory } from "slotworks/components/factories/matrix-factory";
import { AbstractMatrixComponent } from "slotworks/components/matrix/abstract-matrix-component";
import { SpinStageEvents } from "slotworks/components/matrix/reel/transition-behaviours/spin/reel-spinner";
import { TintDimSymbolBehaviour } from "slotworks/components/matrix/symbol/behaviours/tint-dim-symbol-behaviour";
import { slotDefinition } from "slotworks/model/slot-definition";

export class TGRMatrixFactory extends MatrixFactory {

    constructor() {
        super();

        const minStopDistance = slotDefinition.matrixGrid.reduce((max, current) => current > max ? current : max, 0) + 1;

        this.spinSpeed = 0.025;

        this.defaultReelset = "base";
        this.defaultStops = [0, 0, 0, 0, 0];

        this.spinStages = [
            {
                name: "pullback",
                s: 0.25,
                t: 310,
                easing: "Quadratic.Out",
                events: [
                    {
                        name: SpinStageEvents.START,
                        delay: 200
                    }
                ]
            },
            {
                name: "spin_normal",
                s: -0,
                u: 0,
                v: -this.spinSpeed
            },
            {
                name: "accelerate",
                s: -1.25,
                u: 0,
                v: -this.spinSpeed
            },
            {
                name: "minRepeat",
                s: -1,
                u: -this.spinSpeed,
                v: -this.spinSpeed
            },
            {
                name: "repeat",
                s: -1,
                u: -this.spinSpeed,
                v: -this.spinSpeed,
                events: [
                    {
                        name: SpinStageEvents.START,
                        delay: 0
                    }
                ]
            },
            {
                name: "stop",
                s: -minStopDistance,
                u: -this.spinSpeed,
                v: -this.spinSpeed,
                events: [
                    {
                        name: SpinStageEvents.STOP,
                        delay: 200
                    }
                ]
            },
            {
                name: "land",
                s: -0.35,
                u: -0.01,
                v: 0
            },
            {
                name: "complete",
                s: 0.35,
                t: 220,
                easing: "Quadratic.Out",
                events: [
                    {
                        name: SpinStageEvents.SKIP,
                        delay: 200
                    }
                ]
            }
        ];

        this.quickSpinStages = [
            {
                name: "pullback",
                s: 0.25,
                t: 150,
                easing: "Quadratic.Out",
                events: [
                    {
                        name: SpinStageEvents.START,
                        delay: 20
                    }
                ]
            },
            {
                name: "spin_turbo",
                s: -0,
                u: 0,
                v: -this.spinSpeed
            },
            {
                name: "accelerate",
                s: -1.25,
                u: 0,
                v: -this.spinSpeed * 2
            },
            {
                name: "minRepeat",
                s: -1,
                u: -this.spinSpeed * 2,
                v: -this.spinSpeed * 2
            },
            {
                name: "repeat",
                s: -1,
                u: -this.spinSpeed * 2,
                v: -this.spinSpeed * 2,
                events: [
                    {
                        name: SpinStageEvents.START,
                        delay: 0
                    }
                ]
            },
            {
                name: "stop",
                s: -minStopDistance,
                u: -this.spinSpeed * 2,
                v: -this.spinSpeed * 2,
                events: [
                    {
                        name: SpinStageEvents.STOP,
                        delay: 150
                    }
                ]
            },
            {
                name: "land",
                s: -0.35,
                u: -0.01,
                v: 0
            },
            {
                name: "complete",
                s: 0.35,
                t: 220,
                easing: "Quadratic.Out",
                events: [
                    {
                        name: SpinStageEvents.SKIP,
                        delay: 100
                    }
                ]
            }
        ];

        this.skipStages = [
            {
                name: "spin_superturbo",
                s: 0,
                t: 0
            },
            {
                name: "stop",
                s: -minStopDistance,
                t: 0,
                events: [
                    {
                        name: SpinStageEvents.STOP,
                        delay: 0
                    },
                    {
                        name: SpinStageEvents.SKIP,
                        delay: 0
                    }
                ]
            },
            {
                name: "land_turbo",
                s: 0,
                t: 0
            },
            {
                name: "complete",
                s: 0,
                t: 0
            }
        ];

        this.anticipationStages = [
            {
                name: "anticipate",
                s: -10,
                u: -this.spinSpeed,
                v: -this.spinSpeed * 0.4
            },
            {
                name: "tease",
                s: -10,
                u: -this.spinSpeed * 0.4,
                v: -this.spinSpeed * 0.4
            },
            {
                name: "stop",
                s: -minStopDistance,
                u: -this.spinSpeed * 0.4,
                v: 0.0
            },
            {
                name: "land",
                s: -0.01,
                t: 50
            },
            {
                name: "complete",
                s: 0.01,
                t: 110,
                events: [
                    {
                        name: SpinStageEvents.STOP,
                        delay: 100
                    },
                    {
                        name: SpinStageEvents.SKIP,
                        delay: 0
                    }
                ]
            }
        ];
    }

    protected createMatrix(): AbstractMatrixComponent {
        PartnerConnectManager.OVERRIDE_INITIAL_GRID = [
            ["H4", "L4", "L4", "L5"],
            ["L2", "L2", "H4", "L1"],
            ["H5", "L4", "L1", "H5"],
            ["H4", "L1", "L1", "H3"],
            ["L5", "L5", "L1", "L1"]
        ];

        return new TGRMatrixComponent(
            slotDefinition.matrixGrid,
            this.defaultStops,
            slotDefinition.reelsets.get(this.defaultReelset),
            null,
            null,
            TGRSymbolComponent
        );
    }

    protected addSymbolBehaviours(matrix: AbstractMatrixComponent) {
        matrix.addDefaultSymbolBehaviour(TGRSymbolSpineAnimateBehaviour, [
            "S", "W", "C", "QH",
            "H1_14", "H1_13", "H1_12",
            "H1", "H2", "H3", "H4", "H5",
            "L1", "L2", "L3", "L4", "L5"
        ]);
        TintDimSymbolBehaviour.DIM_TINT = 0x555555;
        matrix.addDefaultSymbolBehaviour(TintDimSymbolBehaviour, [
            "S", "W", "C",
            "QH_1000",
            "QH_200",
            "QH_100",
            "QH_50",
            "QH_20",
            "QH_18",
            "QH_15",
            "QH_12",
            "QH_10",
            "QH_8",
            "QH_6",
            "QH_5",
            "QH_3",
            "QH_2",
            "QH_1",
            "QH",
            "H1_14", "H1_13", "H1_12",
            "H1", "H2", "H3", "H4", "H5",
            "L1", "L2", "L3", "L4", "L5"
        ]);
    }
}
